import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  MessageSquare, 
  Mail, 
  User, 
  Clock, 
  Trash2,
  CheckCircle,
  AlertCircle,
  Loader2,
  Eye,
  Send,
  Reply
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { API, API_URL } from '@/config';

const SupportTicketsManagement = () => {
  const { t, i18n } = useTranslation();
  const { token } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [replyModalOpen, setReplyModalOpen] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [adminNotes, setAdminNotes] = useState('');

  const statusOptions = [
    { 
      value: 'all', 
      label: { en: 'All Tickets', ar: 'جميع الرسائل', sv: 'Alla ärenden' },
      color: 'bg-slate-100 text-slate-800'
    },
    { 
      value: 'new', 
      label: { en: 'New', ar: 'جديد', sv: 'Ny' },
      color: 'bg-blue-100 text-blue-800'
    },
    { 
      value: 'in_progress', 
      label: { en: 'In Progress', ar: 'قيد المعالجة', sv: 'Pågår' },
      color: 'bg-yellow-100 text-yellow-800'
    },
    { 
      value: 'replied', 
      label: { en: 'Replied', ar: 'تم الرد', sv: 'Besvarad' },
      color: 'bg-purple-100 text-purple-800'
    },
    { 
      value: 'resolved', 
      label: { en: 'Resolved', ar: 'تم الحل', sv: 'Löst' },
      color: 'bg-green-100 text-green-800'
    }
  ];

  useEffect(() => {
    fetchTickets();
  }, []);

  useEffect(() => {
    filterTickets();
  }, [tickets, statusFilter]);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/support-tickets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTickets(response.data || []);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      toast.error(i18n.language === 'ar' ? 'خطأ في تحميل الرسائل' : 'Error loading tickets');
    } finally {
      setLoading(false);
    }
  };

  const filterTickets = () => {
    if (statusFilter === 'all') {
      setFilteredTickets(tickets);
    } else {
      setFilteredTickets(tickets.filter(ticket => ticket.status === statusFilter));
    }
  };

  const handleOpenTicket = (ticket) => {
    setSelectedTicket(ticket);
    setAdminNotes(ticket.admin_notes || '');
    setModalOpen(true);
  };

  const handleOpenReplyModal = (ticket) => {
    setSelectedTicket(ticket);
    setReplyMessage('');
    setReplyModalOpen(true);
  };

  const handleSendReply = async () => {
    if (!selectedTicket || !replyMessage.trim()) {
      toast.error(i18n.language === 'ar' ? 'الرجاء كتابة رسالة الرد' : 'Please enter a reply message');
      return;
    }

    setSendingReply(true);
    try {
      await axios.post(
        `${API}/admin/support-tickets/${selectedTicket.id}/reply`,
        {
          message: replyMessage.trim(),
          language: i18n.language || 'en'
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success(
        i18n.language === 'ar' 
          ? 'تم إرسال الرد بنجاح! ✉️' 
          : i18n.language === 'sv'
          ? 'Svar skickat! ✉️'
          : 'Reply sent successfully! ✉️'
      );
      setReplyModalOpen(false);
      setReplyMessage('');
      fetchTickets();
    } catch (error) {
      console.error('Error sending reply:', error);
      toast.error(
        i18n.language === 'ar' 
          ? 'خطأ في إرسال الرد' 
          : 'Error sending reply'
      );
    } finally {
      setSendingReply(false);
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    if (!selectedTicket) return;

    try {
      await axios.put(
        `${API}/admin/support-tickets/${selectedTicket.id}`,
        null,
        {
          params: {
            status: newStatus,
            admin_notes: adminNotes || undefined
          },
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success(i18n.language === 'ar' ? 'تم تحديث الحالة! ✅' : 'Status updated! ✅');
      setModalOpen(false);
      fetchTickets();
    } catch (error) {
      console.error('Error updating ticket:', error);
      toast.error(i18n.language === 'ar' ? 'خطأ في التحديث' : 'Error updating');
    }
  };

  const handleDelete = async (ticketId) => {
    if (!window.confirm(i18n.language === 'ar' ? 'هل أنت متأكد من حذف هذه الرسالة؟' : 'Are you sure you want to delete this ticket?')) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/support-tickets/${ticketId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(i18n.language === 'ar' ? 'تم الحذف! ✅' : 'Deleted! ✅');
      fetchTickets();
    } catch (error) {
      console.error('Error deleting ticket:', error);
      toast.error(i18n.language === 'ar' ? 'خطأ في الحذف' : 'Error deleting');
    }
  };

  const getStatusBadge = (status) => {
    const statusOption = statusOptions.find(s => s.value === status);
    if (!statusOption) return null;
    
    return (
      <Badge className={statusOption.color}>
        {statusOption.label[i18n.language] || statusOption.label.en}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString(i18n.language === 'ar' ? 'ar-SA' : i18n.language === 'sv' ? 'sv-SE' : 'en-US');
  };

  const stats = {
    total: tickets.length,
    new: tickets.filter(t => t.status === 'new').length,
    in_progress: tickets.filter(t => t.status === 'in_progress').length,
    replied: tickets.filter(t => t.status === 'replied').length,
    resolved: tickets.filter(t => t.status === 'resolved').length
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-slate-900">
          {i18n.language === 'ar' ? '💬 إدارة رسائل الدعم' : i18n.language === 'sv' ? '💬 Hantera supportärenden' : '💬 Support Tickets Management'}
        </h2>
        <p className="text-slate-600 mt-1">
          {i18n.language === 'ar' ? 'عرض وإدارة جميع رسائل الدعم من المستخدمين' : i18n.language === 'sv' ? 'Visa och hantera alla supportärenden från användare' : 'View and manage all support messages from users'}
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="p-4 bg-gradient-to-br from-blue-50 to-blue-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-600 font-medium">
                {i18n.language === 'ar' ? 'إجمالي الرسائل' : i18n.language === 'sv' ? 'Totalt' : 'Total'}
              </p>
              <p className="text-3xl font-bold text-blue-900">{stats.total}</p>
            </div>
            <MessageSquare className="w-10 h-10 text-blue-500 opacity-50" />
          </div>
        </Card>

        <Card className="p-4 bg-gradient-to-br from-yellow-50 to-yellow-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-yellow-600 font-medium">
                {i18n.language === 'ar' ? 'جديد' : i18n.language === 'sv' ? 'Ny' : 'New'}
              </p>
              <p className="text-3xl font-bold text-yellow-900">{stats.new}</p>
            </div>
            <AlertCircle className="w-10 h-10 text-yellow-500 opacity-50" />
          </div>
        </Card>

        <Card className="p-4 bg-gradient-to-br from-orange-50 to-orange-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-orange-600 font-medium">
                {i18n.language === 'ar' ? 'قيد المعالجة' : i18n.language === 'sv' ? 'Pågår' : 'In Progress'}
              </p>
              <p className="text-3xl font-bold text-orange-900">{stats.in_progress}</p>
            </div>
            <Clock className="w-10 h-10 text-orange-500 opacity-50" />
          </div>
        </Card>

        <Card className="p-4 bg-gradient-to-br from-purple-50 to-purple-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-purple-600 font-medium">
                {i18n.language === 'ar' ? 'تم الرد' : i18n.language === 'sv' ? 'Besvarad' : 'Replied'}
              </p>
              <p className="text-3xl font-bold text-purple-900">{stats.replied}</p>
            </div>
            <Reply className="w-10 h-10 text-purple-500 opacity-50" />
          </div>
        </Card>

        <Card className="p-4 bg-gradient-to-br from-green-50 to-green-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-600 font-medium">
                {i18n.language === 'ar' ? 'تم الحل' : i18n.language === 'sv' ? 'Löst' : 'Resolved'}
              </p>
              <p className="text-3xl font-bold text-green-900">{stats.resolved}</p>
            </div>
            <CheckCircle className="w-10 h-10 text-green-500 opacity-50" />
          </div>
        </Card>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-3">
        <label className="font-medium text-slate-700">
          {i18n.language === 'ar' ? 'تصفية حسب الحالة:' : i18n.language === 'sv' ? 'Filtrera efter status:' : 'Filter by status:'}
        </label>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {statusOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label[i18n.language] || option.label.en}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Tickets List */}
      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-emerald-500" />
        </div>
      ) : filteredTickets.length === 0 ? (
        <Card className="p-12 text-center">
          <MessageSquare className="w-16 h-16 mx-auto text-slate-300 mb-4" />
          <p className="text-slate-500">
            {i18n.language === 'ar' ? 'لا توجد رسائل دعم' : i18n.language === 'sv' ? 'Inga supportärenden' : 'No support tickets'}
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {filteredTickets.map((ticket) => (
            <Card key={ticket.id} className="p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between gap-4 flex-wrap md:flex-nowrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    {getStatusBadge(ticket.status)}
                    <span className="text-xs text-slate-500">
                      {formatDate(ticket.created_at)}
                    </span>
                  </div>
                  
                  <h3 className="font-bold text-lg text-slate-900 mb-2">
                    {ticket.subject}
                  </h3>
                  
                  <div className="flex items-center gap-4 text-sm text-slate-600 mb-3">
                    <div className="flex items-center gap-1">
                      <User className="w-4 h-4" />
                      <span>{ticket.name}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Mail className="w-4 h-4" />
                      <span>{ticket.email}</span>
                    </div>
                  </div>
                  
                  <p className="text-slate-600 line-clamp-2">
                    {ticket.message}
                  </p>
                </div>

                <div className="flex gap-2 flex-shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleOpenReplyModal(ticket)}
                    className="whitespace-nowrap bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border-emerald-200"
                    data-testid={`reply-ticket-${ticket.id}`}
                  >
                    <Reply className="w-4 h-4 mr-1" />
                    {i18n.language === 'ar' ? 'رد' : i18n.language === 'sv' ? 'Svara' : 'Reply'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleOpenTicket(ticket)}
                    className="whitespace-nowrap"
                    data-testid={`view-ticket-${ticket.id}`}
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    {i18n.language === 'ar' ? 'عرض' : i18n.language === 'sv' ? 'Visa' : 'View'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(ticket.id)}
                    className="text-red-600 hover:bg-red-50 flex-shrink-0"
                    data-testid={`delete-ticket-${ticket.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Ticket Details Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {i18n.language === 'ar' ? 'تفاصيل الرسالة' : i18n.language === 'sv' ? 'Ärendedetaljer' : 'Ticket Details'}
            </DialogTitle>
          </DialogHeader>

          {selectedTicket && (
            <div className="space-y-6">
              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  {i18n.language === 'ar' ? 'الحالة' : i18n.language === 'sv' ? 'Status' : 'Status'}
                </label>
                <Select 
                  value={selectedTicket.status} 
                  onValueChange={(value) => setSelectedTicket({...selectedTicket, status: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {statusOptions.filter(s => s.value !== 'all').map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label[i18n.language] || option.label.en}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Subject */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {i18n.language === 'ar' ? 'الموضوع' : i18n.language === 'sv' ? 'Ämne' : 'Subject'}
                </label>
                <p className="text-slate-900 font-semibold">{selectedTicket.subject}</p>
              </div>

              {/* User Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    {i18n.language === 'ar' ? 'الاسم' : i18n.language === 'sv' ? 'Namn' : 'Name'}
                  </label>
                  <p className="text-slate-900">{selectedTicket.name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    {i18n.language === 'ar' ? 'البريد الإلكتروني' : i18n.language === 'sv' ? 'E-post' : 'Email'}
                  </label>
                  <p className="text-slate-900">{selectedTicket.email}</p>
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {i18n.language === 'ar' ? 'الرسالة' : i18n.language === 'sv' ? 'Meddelande' : 'Message'}
                </label>
                <Card className="p-4 bg-slate-50">
                  <p className="text-slate-900 whitespace-pre-wrap">{selectedTicket.message}</p>
                </Card>
              </div>

              {/* Attachments */}
              {selectedTicket.attachments && selectedTicket.attachments.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {i18n.language === 'ar' ? 'الصور المرفقة' : i18n.language === 'sv' ? 'Bifogade bilder' : 'Attached Images'}
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    {selectedTicket.attachments.map((url, index) => (
                      <a
                        key={index}
                        href={`${API_URL}${url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block"
                      >
                        <img
                          src={`${API_URL}${url}`}
                          alt={`Attachment ${index + 1}`}
                          className="w-full h-32 object-cover rounded-lg border-2 border-slate-200 hover:border-emerald-400 transition-colors cursor-pointer"
                        />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Admin Notes */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  {i18n.language === 'ar' ? 'ملاحظات المشرف' : i18n.language === 'sv' ? 'Administratörsanteckningar' : 'Admin Notes'}
                </label>
                <Textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder={i18n.language === 'ar' ? 'أضف ملاحظاتك هنا...' : i18n.language === 'sv' ? 'Lägg till dina anteckningar här...' : 'Add your notes here...'}
                  rows={4}
                />
              </div>

              {/* Timestamps */}
              <div className="grid grid-cols-2 gap-4 text-sm text-slate-600">
                <div>
                  <label className="font-medium">{i18n.language === 'ar' ? 'تاريخ الإنشاء:' : i18n.language === 'sv' ? 'Skapad:' : 'Created:'}</label>
                  <p>{formatDate(selectedTicket.created_at)}</p>
                </div>
                <div>
                  <label className="font-medium">{i18n.language === 'ar' ? 'آخر تحديث:' : i18n.language === 'sv' ? 'Uppdaterad:' : 'Updated:'}</label>
                  <p>{formatDate(selectedTicket.updated_at)}</p>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              {i18n.language === 'ar' ? 'إلغاء' : i18n.language === 'sv' ? 'Avbryt' : 'Cancel'}
            </Button>
            <Button 
              onClick={() => handleUpdateStatus(selectedTicket?.status)}
              className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
            >
              {i18n.language === 'ar' ? 'حفظ التغييرات' : i18n.language === 'sv' ? 'Spara ändringar' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reply Modal */}
      <Dialog open={replyModalOpen} onOpenChange={setReplyModalOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Reply className="w-5 h-5 text-emerald-600" />
              {i18n.language === 'ar' ? 'الرد على التذكرة' : i18n.language === 'sv' ? 'Svara på ärende' : 'Reply to Ticket'}
            </DialogTitle>
          </DialogHeader>

          {selectedTicket && (
            <div className="space-y-4">
              {/* Original Message Summary */}
              <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                <div className="flex items-center gap-2 text-sm text-slate-600 mb-2">
                  <User className="w-4 h-4" />
                  <span className="font-medium">{selectedTicket.name}</span>
                  <span className="text-slate-400">•</span>
                  <Mail className="w-4 h-4" />
                  <span>{selectedTicket.email}</span>
                </div>
                <p className="text-sm font-medium text-slate-800 mb-1">{selectedTicket.subject}</p>
                <p className="text-sm text-slate-600 line-clamp-3">{selectedTicket.message}</p>
              </div>

              {/* Previous Replies */}
              {selectedTicket.replies && selectedTicket.replies.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {i18n.language === 'ar' ? 'الردود السابقة' : i18n.language === 'sv' ? 'Tidigare svar' : 'Previous Replies'}
                  </label>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {selectedTicket.replies.map((reply, index) => (
                      <div key={reply.id || index} className="bg-emerald-50 rounded-lg p-3 border border-emerald-200">
                        <div className="flex items-center gap-2 text-xs text-emerald-700 mb-1">
                          <Send className="w-3 h-3" />
                          <span>{new Date(reply.sent_at).toLocaleString(i18n.language === 'ar' ? 'ar-SA' : i18n.language === 'sv' ? 'sv-SE' : 'en-US')}</span>
                        </div>
                        <p className="text-sm text-emerald-900">{reply.message}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Reply Input */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  {i18n.language === 'ar' ? 'رسالة الرد' : i18n.language === 'sv' ? 'Ditt svar' : 'Your Reply'}
                </label>
                <Textarea
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  placeholder={
                    i18n.language === 'ar' 
                      ? 'اكتب ردك هنا... سيتم إرساله عبر البريد الإلكتروني للمستخدم' 
                      : i18n.language === 'sv'
                      ? 'Skriv ditt svar här... Det skickas via e-post till användaren'
                      : 'Type your reply here... It will be sent via email to the user'
                  }
                  rows={5}
                  className="resize-none"
                  data-testid="reply-message-input"
                />
              </div>

              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Mail className="w-4 h-4" />
                <span>
                  {i18n.language === 'ar' 
                    ? `سيتم إرسال الرد إلى: ${selectedTicket.email}` 
                    : i18n.language === 'sv'
                    ? `Svaret skickas till: ${selectedTicket.email}`
                    : `Reply will be sent to: ${selectedTicket.email}`}
                </span>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setReplyModalOpen(false)} disabled={sendingReply}>
              {i18n.language === 'ar' ? 'إلغاء' : i18n.language === 'sv' ? 'Avbryt' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleSendReply}
              disabled={sendingReply || !replyMessage.trim()}
              className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
              data-testid="send-reply-button"
            >
              {sendingReply ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {i18n.language === 'ar' ? 'جاري الإرسال...' : i18n.language === 'sv' ? 'Skickar...' : 'Sending...'}
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  {i18n.language === 'ar' ? 'إرسال الرد' : i18n.language === 'sv' ? 'Skicka svar' : 'Send Reply'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SupportTicketsManagement;
